<template>
    <BOffcanvas v-model="visible" placement="end" :title="$t('releaseNotes.title')">
        <p v-if="!notes.length" class="text-muted">{{ $t('releaseNotes.empty') }}</p>
        <div v-for="note in notes" :key="note.id" class="release-note border-bottom pb-3 mb-3">
            <div class="d-flex align-items-center gap-2 mb-1">
                <span class="badge bg-secondary">{{ productLabel(note.product) }}</span>
                <span class="badge bg-primary release-note-version">{{ note.version }}</span>
                <strong class="flex-grow-1">{{ pickLocalized(note.title, locale) }}</strong>
                <small class="text-muted">{{ formatDate(note.published_at) }}</small>
            </div>
            <div v-if="isHtmlNote(note)" class="release-note-body release-note-html mb-0" v-html="pickLocalized(note.note, locale)"></div>
            <p v-else class="release-note-body mb-0">{{ pickLocalized(note.note, locale) }}</p>
        </div>
    </BOffcanvas>
</template>

<script setup>
import { ref } from 'vue';
import moment from 'moment';
import { BOffcanvas } from 'bootstrap-vue-next';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { pickLocalized } from '@/services/localizedText';

const { t, locale } = useI18n();
const userStore = useUserStore();

const emit = defineEmits(['seen']);

defineExpose({
    show,
});

const visible = ref(false);
const notes = ref([]);

async function show() {
    visible.value = true;
    notes.value = await userStore.loadReleaseNotes();
    if (notes.value.length) {
        const latest = Math.max(...notes.value.map(n => n.id));
        localStorage.setItem('lastSeenReleaseNoteId', String(latest));
        emit('seen');
    }
}

function formatDate(date) {
    return moment(date).format('DD/MM/YYYY');
}

// Produits installateur (valeurs saisies dans le backoffice) : hub, link, lecteurs,
// modules d'extension, espace installateur, application HaxoSetup.
const INSTALLER_PRODUCTS = ['hub', 'link', 'reader', 'extension', 'installerweb', 'haxosetup'];

function productLabel(product) {
    const key = String(product ?? '').toLowerCase();
    return INSTALLER_PRODUCTS.includes(key) ? t(`releaseNotes.products.${key}`) : product;
}

function isHtmlNote(note) {
    return /<[a-z][^>]*>/i.test(pickLocalized(note.note, locale.value) ?? '');
}

</script>

<style scoped>
.release-note-version {
    font-family: monospace;
}

.release-note-body {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.9rem;
}

.release-note-html {
    white-space: normal;
}

.release-note-html :deep(h2) {
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0.6em 0 0.25em;
}

.release-note-html :deep(h3) {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0.5em 0 0.2em;
}

.release-note-html :deep(p) {
    margin: 0.3em 0;
}

.release-note-html :deep(ul),
.release-note-html :deep(ol) {
    padding-left: 1.3em;
    margin: 0.3em 0;
}

.release-note-html :deep(blockquote) {
    border-left: 3px solid #ced4da;
    padding-left: 0.7em;
    color: #6c757d;
    margin: 0.4em 0;
}

.release-note-html :deep(.release-video a) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    padding: 4px 10px;
    background: #ff0000;
    color: #fff;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
}

.release-note-html :deep(.release-video a:hover) {
    background: #cc0000;
    color: #fff;
}
</style>
